<script setup lang="ts">
import { onMounted, ref } from "vue";
import { api } from "../../../../api/Api";
import { DeliveryTracking, Item, RecordStatus } from "../../../../types/models";
import {
  isEmpty,
  formatDate,
  timeSince,
} from "../../../../utils/CommonFunctions";
import DeliveryDetails from "./DelieryDetails.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import { User } from "../../../../types/auth";
import { useScreenSize } from "../../../../utils/useScreenSize";
import MapComponent from "./MapComponent.vue";``
import DataTablePagination from "../../../../components/table/DataTablePagination.vue";
import MessageBoxService from "../../../../services/MessageBox";
import FormHeader from "../../../../components/ui/Templates/FormHeader.vue";
import DropDownButton from "../../../../components/ui/common/DropdownButton.vue";

const { isMobile } = useScreenSize();

const selectedDelivery = ref<DeliveryTracking | null>(null);
const searchQuery = ref("");

const updateStatus = async (
  delivery: DeliveryTracking,
  status: RecordStatus
) => {
  let attachment = null; let remarks = null;
  const confirmed = await MessageBoxService.confirm({
      title: 'Update Delivery',
      message: status.require_attachment ? 'Please upload the Delivery Confimration' : 'Are you sure you want to update the delivery status?',
      confirmText: "Update as " + status.name,
      requireRemarks: status.require_remarks,
      requireAttachments: status.require_attachment,
      showRemarks: true,
      cancelText: 'Cancel',
      type: 'info'
    })
    console.log(confirmed);
    if (confirmed) {
      attachment = confirmed.attachment;
      remarks = confirmed.remarks;
      if (status.require_attachment && !attachment) 
      return;
      if (status.require_remarks && !remarks)
        return;
    } else {
      return;
    }
    
  const params = {
    id: delivery.uid,
    delivery_status_id: status.id,
    attachment: attachment,
    remarks: remarks,
    page: currentPage.value,
    per_page: 25,
    search_query: searchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "update",
    params
  );
  if (response.success == true) {
    deliveryList.value = response.data.data;
    selectedDelivery.value =
      deliveryList.value.find((e) => e.uid == delivery.uid) ?? null;
  }
};

const assignDeliveryStaff = async (delivery: DeliveryTracking, user: User) => {
  const params = {
    id: delivery.uid,
    users_id: user.uid,
    page: currentPage.value,
    per_page: 25,
    search_query: searchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "assign-user",
    params
  );
  if (response.success == true) {
    deliveryList.value = response.data.data;
    selectedDelivery.value =
      deliveryList.value.find((e) => e.uid == delivery.uid) ?? null;
  }
};

const items = ref<Item[]>([]);
const handleDeliverySelect = async (delivery: DeliveryTracking) => {
  selectedDelivery.value = delivery;
  items.value = [];
  const params = {
    id: delivery.uid,
    page: currentPage.value,
    per_page: 25,
    search_query: searchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "items",
    params
  );
  if (response.success == true) {
    items.value = response.data;
  }
};

const endPoint = "/clients/revenue/sales/delivery-trackings/";

const saveLocation = async (lat: number, lng: number) => {
  if (selectedDelivery.value) {
    const params = {
    id: selectedDelivery.value.uid,
    lat: lat,
    lng: lng,
    page: currentPage.value,
    per_page: 25,
    search_query: searchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "update-cordinates",
    params
  );
  if (response.success == true) {
    deliveryList.value = response.data.data;
    selectedDelivery.value.lat = lat;
    selectedDelivery.value.lng = lng;
  }
    
  }
};
const getPendingStatus = (delivery: DeliveryTracking) => {
  let status = record_status.value.find(
    (e) => e.linked_index !== undefined && e.linked_index >= 1
  );
  let res = record_status.value.filter((e) => e.id > (delivery.status_id ?? 0) && e.name !== 'Delivery Assigned');
  return status && !isEmpty(status)
    ? res.filter((e) => e.linked_index != status.linked_index)
    : res;
};

const deliveryList = ref<DeliveryTracking[]>([]);
const record_status = ref<RecordStatus[]>([]);
const delivery_users = ref<User[]>([]);
const getList = async () => {
  const params = {
    status: "",
    page: currentPage.value,
    per_page: 25,
    search_query: searchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "get",
    params
  );
  if (response.success == true) {
    record_status.value = response.lists.status;
    delivery_users.value = response.lists.users;
    deliveryList.value = response.data.data;
    currentPage.value = response.data.current_page;
    totalPages.value = Math.ceil(response.data.total / response.data.per_page);
  }
};

const currentPage = ref(1);
const totalPages = ref(1);

const handlePageChange = (page: number) => {
  currentPage.value = page;
  getList();
};


const isFullScreen = ref(false);

onMounted(() => {
  getList();
});
</script>

<template>
  <div
    class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-100 dark:border-gray-800"
  >
    <!-- Header -->
    <div class="p-6 border-b border-gray-100 dark:border-gray-800">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
        Delivery Tracking
      </h1>
    </div>

    <div class="flex flex-col lg:flex-row">
      <!-- Deliveries List -->
      <div
        class="w-full lg:w-1/3 border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-800"
      >
        <div class="p-4">
          <div class="relative mb-4">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              >🔍</span
            >
            <input
              v-model="searchQuery"
              @keyup.enter="getList"
              type="search"
              placeholder="Search deliveries..."
              class="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
            />
          </div>

          <div class="space-y-3">
            <div
              v-for="delivery in deliveryList"
              :key="delivery.uid"
              @click="handleDeliverySelect(delivery)"
              class="w-full p-4 rounded-lg border transition-colors cursor-pointer"
              :class="[
                selectedDelivery?.uid === delivery.uid
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800',
              ]"
            >
              <div class="flex justify-between items-start mb-2">
                <div class="text-left">
                  <h3 class="font-medium text-gray-900 dark:text-white">
                    {{
                      !isEmpty(delivery.sales_invoices_id)
                        ? "Invoice #:"
                        : "Delivery #:"
                    }}
                    {{ delivery.related_document_no }}
                  </h3>
                </div>
                <span
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :style="{ backgroundColor: delivery?.status_color }"
                >
                  {{ delivery.status }}
                </span>
              </div>
              <div class="flex justify-between items-start mb-2">
                <div class="text-sm text-gray-600 dark:text-gray-400">
                  {{ delivery.customer_name }}
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">
                  {{ delivery.customer_mobile }}
                </div>
              </div>
              <div class="flex justify-between items-start mb-2">
                <h4 class="font-medium text-gray-900 dark:text-white flex">
                  <IconComponent name="House" class="mr-1" />
                  {{ delivery.full_address }}
                </h4>
              </div>
              <div class="flex justify-between items-start mb-2">
                <div class="text-left">
                  <h3 class="font-medium text-gray-900 dark:text-white flex">
                    <IconComponent name="UsersRound" class="mr-1" />
                    {{ delivery.assigned_to_user }}
                  </h3>
                </div>
                <div class="text-right">
                  <span class="text-sm text-gray-600 dark:text-gray-400">{{
                    timeSince(delivery.created_at)
                  }}</span>
                </div>
              </div>
              <div class="flex justify-between items-center">
                <div class="text-sm text-left text-gray-500 dark:text-gray-400">
                  Deliver On: {{ delivery.delivery_date ? formatDate(delivery.delivery_date) : '' }}
                </div>
                <div @click.stop>
                  <DropDownButton
                    :hideArrow="true"
                    icon="UsersRound"
                    v-if="delivery.assigned_to_user == null"
                  >
                    <button
                      @click="assignDeliveryStaff(delivery, user)"
                      v-for="user in delivery_users"
                      class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {{ user.name }}
                    </button>
                  </DropDownButton>
                  <DropDownButton :hideArrow="true" v-else>
                    <button
                      @click="updateStatus(delivery, status)"
                      v-for="status in getPendingStatus(delivery)"
                      class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <IconComponent :name="status.icon_css" />
                      {{ status.name }}
                    </button>
                  </DropDownButton>
                </div>
              </div>
            </div>
          </div>

          <DataTablePagination
            :current-page="currentPage"
            :total-pages="totalPages"      
            @page-change="handlePageChange"
          />
        </div>
      </div>

      <!-- Delivery Details -->
      <div class="flex-1 p-6" v-if="selectedDelivery && !isMobile" >
        <div class="space-y-6">
        <DeliveryDetails :selected-delivery="selectedDelivery" :record-status="record_status" :items="items" :show-steps="true"
        :pending="getPendingStatus(selectedDelivery)" :delivery-users="delivery_users" @delivery="assignDeliveryStaff" @status="updateStatus"/>
        <MapComponent :lat="selectedDelivery?.lat" :lng="selectedDelivery?.lng" @save="saveLocation" :fullscreen="false"/>
      </div>
      </div>


      <div
       v-else-if="selectedDelivery && isMobile"
    class=" backdrop-blur-sm inset-0 fixed bg-black/50 flex items-center justify-center z-[900] md:p-0 pt-0"
  >
    <div
    
      class="w-full bg-white dark:bg-gray-800 md:rounded-lg rounded-md shadow-xl mx-0 flex flex-col h-full md:max-h-[90vh]"
    >
      <!-- Fixed Header -->
      <FormHeader
        mode="view"
        :showEdit="false"
        endPoint="endPoint"
        title="Track Delivery"
        :onTour="false"
        :modal="true"
        :noPermission="false"
        @close="selectedDelivery = null"
      >
      <template #form-header-buttons>
        <div class="flex justify-between items-center">
          <button
                @click="isFullScreen = true"
                class="text-gray-400 hover:text-gray-500"
              >
                <IconComponent name="MapPinned" />
              </button>
                  <DropDownButton
                    :hideArrow="true"
                    icon="UsersRound"
                    v-if="selectedDelivery.assigned_to_user == null"
                  >
                    <button
                      @click="assignDeliveryStaff(selectedDelivery, user)"
                      v-for="user in delivery_users"
                      class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {{ user.name }}
                    </button>
                  </DropDownButton>
                  <DropDownButton :hideArrow="true" v-else>
                    <button
                      @click="updateStatus(selectedDelivery, status)"
                      v-for="status in getPendingStatus(selectedDelivery)"
                      class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <IconComponent :name="status.icon_css" />
                      {{ status.name }}
                    </button>
                  </DropDownButton>
        </div>
        </template>
      </FormHeader>

      <!-- Split View Container -->
      <div
        class="flex-1 flex min-h-0"
      >
        <!-- Left Side - Form -->
        <div
          class="flex md:flex-col flex-1 w-full"
        >
          <!-- Scrollable Form Content -->
          <div class="md:p-6 overflow-y-auto flex-1" id="form-fields">
            <DeliveryDetails :selected-delivery="selectedDelivery" :record-status="record_status" :items="items"  :show-steps="false" 
            :pending="getPendingStatus(selectedDelivery)" :delivery-users="delivery_users" @delivery="assignDeliveryStaff" @status="updateStatus"/>
              <MapComponent :lat="selectedDelivery?.lat" :lng="selectedDelivery?.lng"  @save="saveLocation" :fullscreen="isFullScreen" @close="isFullScreen = false"/>
          </div>
        </div>
      </div>
    </div>
  </div>
    </div>    
  </div>
</template>
